export const NATIONAL_ID_TYPE = "NATIONAL_IDENTIFICATION_NUMBER" as const;

export type PersonnummerProblem =
  | "empty"
  | "length"
  | "nonDigit"
  | "century"
  | "date"
  | "future"
  | "control"
  | "ambiguous";

export type PersonnummerResult =
  | {
      ok: true;
      value: string;
      birthDate: string;
      age: number;
      display: string;
    }
  | { ok: false; problem: PersonnummerProblem };

export function luhnCheckDigit(digits: string): number {
  let sum = 0;
  for (let i = 0; i < digits.length; i++) {
    let d = Number(digits[i]) * (i % 2 === 0 ? 2 : 1);
    if (d > 9) d -= 9;
    sum += d;
  }
  return (10 - (sum % 10)) % 10;
}

function realDate(year: number, month: number, day: number): Date | null {
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const d = new Date(Date.UTC(year, month - 1, day));
  if (Number.isNaN(d.getTime())) return null;
  if (d.getUTCFullYear() !== year || d.getUTCMonth() !== month - 1 || d.getUTCDate() !== day) {
    return null;
  }
  return d;
}

function iso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function wholeYears(birth: Date, now: Date): number {
  let age = now.getUTCFullYear() - birth.getUTCFullYear();
  const todayMD = (now.getUTCMonth() + 1) * 100 + now.getUTCDate();
  const birthMD = (birth.getUTCMonth() + 1) * 100 + birth.getUTCDate();
  if (todayMD < birthMD) age -= 1;
  return age;
}

function checkLong(twelve: string, now: Date): PersonnummerResult {
  if (!/^\d{12}$/.test(twelve)) return { ok: false, problem: "nonDigit" };

  const century = Number(twelve.slice(0, 2));
  if (century < 18 || century > 20) return { ok: false, problem: "century" };

  const nine = twelve.slice(2, 11);
  if (luhnCheckDigit(nine) !== Number(twelve[11])) {
    return { ok: false, problem: "control" };
  }

  const birth = realDate(
    Number(twelve.slice(0, 4)),
    Number(twelve.slice(4, 6)),
    Number(twelve.slice(6, 8)),
  );
  if (!birth) return { ok: false, problem: "date" };
  if (birth.getTime() > now.getTime()) return { ok: false, problem: "future" };

  return {
    ok: true,
    value: twelve,
    birthDate: iso(birth),
    age: wholeYears(birth, now),
    display: `${twelve.slice(0, 8)}-${twelve.slice(8)}`,
  };
}

function placeShort(six: string, separator: "-" | "+", last4: string, now: Date): string | null {
  const underHundred = separator === "-";
  const nowYear = now.getUTCFullYear();
  let century = Math.min(Math.trunc(nowYear / 100), 20);
  for (; century >= 18; century--) {
    const birth = realDate(
      century * 100 + Number(six.slice(0, 2)),
      Number(six.slice(2, 4)),
      Number(six.slice(4, 6)),
    );
    if (!birth || birth.getTime() > now.getTime()) continue;
    const age = wholeYears(birth, now);
    if (age < 0) continue;
    if (underHundred ? age < 100 : age >= 100) {
      return `${century}${six}${last4}`;
    }
  }
  return null;
}

export function validatePersonnummer(raw: string, now: Date = new Date()): PersonnummerResult {
  const compact = (raw ?? "").replace(/\s/g, "");
  if (!compact) return { ok: false, problem: "empty" };

  if (/^\d{12}$/.test(compact)) return checkLong(compact, now);
  if (/^\d{8}[-+]\d{4}$/.test(compact)) {
    return checkLong(compact.slice(0, 8) + compact.slice(9), now);
  }

  let six: string | null = null;
  let last4 = "";
  let separator: "-" | "+" = "-";
  if (/^\d{6}[-+]\d{4}$/.test(compact)) {
    six = compact.slice(0, 6);
    separator = compact[6] as "-" | "+";
    last4 = compact.slice(7);
  } else if (/^\d{10}$/.test(compact)) {
    six = compact.slice(0, 6);
    last4 = compact.slice(6);
  }

  if (six === null) {
    return /^[\d\s+-]*$/.test(raw ?? "")
      ? { ok: false, problem: "length" }
      : { ok: false, problem: "nonDigit" };
  }

  if (luhnCheckDigit(six + last4.slice(0, 3)) !== Number(last4[3])) {
    return { ok: false, problem: "control" };
  }

  const twelve = placeShort(six, separator, last4, now);
  if (twelve === null) {
    const anyReal = [20, 19, 18].some(
      (c) =>
        realDate(
          c * 100 + Number(six.slice(0, 2)),
          Number(six.slice(2, 4)),
          Number(six.slice(4, 6)),
        ) !== null,
    );
    return { ok: false, problem: anyReal ? "ambiguous" : "date" };
  }
  return checkLong(twelve, now);
}
